// WCAG 2.1 Contrast Utilities
// Auto-compute accessible text colors from any background

// Hex to RGB helper
export const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return [0, 0, 0];
  const clean = hex.replace('#', '');
  // Handle shorthand hex (#fff -> #ffffff)
  const full = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(full);
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16),
  ] : [0, 0, 0];
};

// RGB to Hex helper
export const rgbToHex = (r, g, b) =>
  `#${[r, g, b].map(c => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('')}`;

// Parse any color format to hex
export const toHex = (color) => {
  if (!color || typeof color !== 'string') return '#000000';
  if (color.startsWith('#')) return color;

  // Handle rgb/rgba
  const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (rgbMatch) {
    return rgbToHex(parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3]));
  }

  return '#000000';
};

// Calculate relative luminance (WCAG 2.1)
export const getLuminance = (color) => {
  const hex = toHex(color);
  const [r, g, b] = hexToRgb(hex).map(c => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

// Calculate contrast ratio between two colors
export const getContrastRatio = (fg, bg) => {
  const l1 = getLuminance(fg);
  const l2 = getLuminance(bg);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

// Check WCAG AA compliance (4.5:1 for normal text, 3:1 for large text/icons)
export const meetsAA = (fg, bg, isLargeText = false) =>
  getContrastRatio(fg, bg) >= (isLargeText ? 3 : 4.5);

// Check WCAG AAA compliance (7:1 for normal text, 4.5:1 for large text)
export const meetsAAA = (fg, bg, isLargeText = false) =>
  getContrastRatio(fg, bg) >= (isLargeText ? 4.5 : 7);

// Default text colors
const LIGHT_TEXT = '#ffffff';
const DARK_TEXT = '#1a1a2e';

// Auto-select best contrast text color for any background
export const getContrastText = (bgColor, options = {}) => {
  const { light = LIGHT_TEXT, dark = DARK_TEXT } = options;
  const bg = toHex(bgColor);
  const lightContrast = getContrastRatio(light, bg);
  const darkContrast = getContrastRatio(dark, bg);
  return darkContrast > lightContrast ? dark : light;
};

// Secondary text with appropriate alpha that maintains readability
export const getContrastTextSecondary = (bgColor) => {
  const primary = getContrastText(bgColor);
  return primary === LIGHT_TEXT ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)';
};

// Muted text for less important content
export const getContrastTextMuted = (bgColor) => {
  const primary = getContrastText(bgColor);
  return primary === LIGHT_TEXT ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)';
};

// Disabled text
export const getContrastTextDisabled = (bgColor) => {
  const primary = getContrastText(bgColor);
  return primary === LIGHT_TEXT ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)';
};

// Lighten a hex color by amount (0-1)
export const lighten = (color, amount) => {
  const hex = toHex(color);
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    r + (255 - r) * amount,
    g + (255 - g) * amount,
    b + (255 - b) * amount
  );
};

// Darken a hex color by amount (0-1)
export const darken = (color, amount) => {
  const hex = toHex(color);
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    r * (1 - amount),
    g * (1 - amount),
    b * (1 - amount)
  );
};

// Adjust foreground color to meet minimum contrast ratio against background
export const ensureContrast = (fgColor, bgColor, minRatio = 4.5) => {
  const fg = toHex(fgColor);
  const bg = toHex(bgColor);

  let ratio = getContrastRatio(fg, bg);
  if (ratio >= minRatio) return fg;

  const bgLum = getLuminance(bg);
  const shouldLighten = bgLum < 0.5;

  let adjusted = fg;
  for (let i = 0; i < 20 && ratio < minRatio; i++) {
    adjusted = shouldLighten ? lighten(adjusted, 0.08) : darken(adjusted, 0.08);
    ratio = getContrastRatio(adjusted, bg);
  }

  // If still not meeting contrast, fall back to white or black
  if (ratio < minRatio) {
    return shouldLighten ? LIGHT_TEXT : DARK_TEXT;
  }

  return adjusted;
};

// Add alpha to hex color (returns hex with alpha: #rrggbbaa)
export const withAlpha = (color, alpha) => {
  const hex = toHex(color);
  const alphaHex = Math.round(alpha * 255).toString(16).padStart(2, '0');
  return `${hex}${alphaHex}`;
};

// Mix two colors together
export const mix = (color1, color2, weight = 0.5) => {
  const [r1, g1, b1] = hexToRgb(toHex(color1));
  const [r2, g2, b2] = hexToRgb(toHex(color2));
  return rgbToHex(
    r1 * weight + r2 * (1 - weight),
    g1 * weight + g2 * (1 - weight),
    b1 * weight + b2 * (1 - weight)
  );
};

// Check if a color is considered "dark" (luminance < 0.5)
export const isDark = (color) => getLuminance(color) < 0.5;

// Check if a color is considered "light"
export const isLight = (color) => getLuminance(color) >= 0.5;
